import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const OrderForm = () => {
  const sectionRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(formRef.current.querySelectorAll('.form-field'),
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.5,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: formRef.current,
            start: 'top 70%',
            toggleActions: 'play none none none'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="order-form" ref={sectionRef} className="py-20 px-4 bg-white">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get Your Custom Quote
          </h2>
          <p className="text-xl text-gray-600">
            Let’s make your next fundraiser sweeter. Tell us a bit about your organisation and event, and we will get back to you with pricing, delivery, and partnership details.
          </p>
        </div>

        {/* Netlify Forms-compatible HTML POST */}
        <form 
          ref={formRef}
          name="order-request"
          method="POST"
          action="/thank-you"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          className="space-y-6 bg-gradient-to-br from-amber-50 to-pink-50 p-8 rounded-3xl shadow-lg"
        >
          <input type="hidden" name="form-name" value="order-request" />
          
          {/* Honeypot anti-spam field for Netlify */}
          <input type="text" name="bot-field" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />
          
          <div className="form-field">
            <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">Full Name *</label>
            <input
              id="name"
              name="name"
              type="text"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-300"
              placeholder="Your name"
            />
          </div>

          <div className="form-field">
            <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">Email *</label>
            <input
              id="email"
              name="email"
              type="email"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-300"
              placeholder="your.email@example.com"
            />
          </div>

          <div className="form-field">
            <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">Phone (Optional)</label>
            <input
              id="phone"
              name="phone"
              type="tel"
              pattern="^[+0-9()\\s-]{7,}$"
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-300"
              placeholder="e.g., +1 (555) 123-4567"
            />
            <p className="text-sm text-gray-500 mt-1">Include a phone number if you'd like us to call you.</p>
          </div>

          <div className="form-field">
            <label htmlFor="organization" className="block text-gray-700 font-semibold mb-2">Organization / Event Name *</label>
            <input
              id="organization"
              name="organization"
              type="text"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-300"
              placeholder="School, company, or event name"
            />
          </div>

          <div className="form-field">
            <label htmlFor="quantity" className="block text-gray-700 font-semibold mb-2">Quantity (boxes) *</label>
            <input
              id="quantity"
              name="quantity"
              type="text"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-300"
              placeholder="e.g., 10-20 boxes, 50 boxes, etc."
            />
            <p className="text-sm text-gray-500 mt-1">1 box = 4 donuts. Minimum order: 5 boxes</p>
          </div>

          <div className="form-field">
            <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">Additional Message (Optional)</label>
            <textarea
              id="message"
              name="message"
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-300 resize-none"
              placeholder="Any special requests, preferred dates, charity details, or questions?"
            />
            <p className="text-sm text-gray-500 mt-1">Please include your preferred event date and the name of your charity.</p>
          </div>

          <div className="form-field">
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl font-semibold"
            >
              Send My Request
            </button>
          </div>

          <p className="text-center text-sm text-gray-600">
            Minimum order: 5 boxes (20 donuts). Wholesale pricing applies.
          </p>
        </form>
      </div>
    </section>
  );
};

export default OrderForm;